import { z } from 'zod';

export const CreatePlaceStorageBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  code: z.string().trim().max(30).nullable().optional(),
  parentId: z.uuid().nullable().optional(),
  storageType: z.enum(['LOCAL', 'CENTRAL', 'THIRD_PARTY', 'VIRTUAL']).default('LOCAL'),
});

export const CreatePlaceStorageResponseSchema = z.object({
  placeStorageId: z.uuid(),
});

export const PlaceStorageListItemSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  name: z.string(),
  code: z.string().nullable(),
  parentId: z.uuid().nullable(),
  storageType: z.enum(['LOCAL', 'CENTRAL', 'THIRD_PARTY', 'VIRTUAL']),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const ListPlaceStoragesResponseSchema = z.object({
  items: z.array(PlaceStorageListItemSchema),
});

const storageAllocationSchema = z.object({
  placeStorageId: z.uuid(),
  quantity: z.string().regex(/^\d+(\.\d{1,3})?$/),
});

export const RegisterLotEntryBodySchema = z.object({
  placeId: z.uuid(),
  tenantProductId: z.uuid(),
  purchaseQuantity: z.string().regex(/^\d+(\.\d{1,3})?$/),
  conversionFactor: z
    .string()
    .regex(/^\d+(\.\d{1,4})?$/)
    .optional(),
  lotCode: z.string().trim().min(1).max(50).optional(),
  lotOrigin: z.enum(['INFORMED', 'SYSTEM_GENERATED']).optional(),
  ownershipType: z.enum(['OWNED', 'THIRD_PARTY', 'VIRTUAL']).optional(),
  thirdPartyPartyId: z.uuid().nullable().optional(),
  supplierRoleId: z.uuid().nullable().optional(),
  tenantSupplierProductId: z.uuid().nullable().optional(),
  unitCostCents: z.string().nullable().optional(),
  totalCostCents: z.string().nullable().optional(),
  currency: z.string().length(3).optional(),
  invoiceNumber: z.string().nullable().optional(),
  invoiceAccessKey: z.string().nullable().optional(),
  storageAllocations: z.array(storageAllocationSchema).min(1),
});

export const RegisterLotEntryResponseSchema = z.object({
  stockLotId: z.uuid(),
});

export const StockAvailabilityResponseSchema = z.object({
  quantityAvailable: z.string(),
  quantityReserved: z.string(),
  quantityBlocked: z.string(),
});

export type CreatePlaceStorageBody = z.infer<typeof CreatePlaceStorageBodySchema>;
export type CreatePlaceStorageResponse = z.infer<typeof CreatePlaceStorageResponseSchema>;
export type ListPlaceStoragesResponse = z.infer<typeof ListPlaceStoragesResponseSchema>;
export type RegisterLotEntryBody = z.infer<typeof RegisterLotEntryBodySchema>;
export type RegisterLotEntryResponse = z.infer<typeof RegisterLotEntryResponseSchema>;
export type StockAvailabilityResponse = z.infer<typeof StockAvailabilityResponseSchema>;

export const ReserveStockBodySchema = z.object({
  placeId: z.uuid(),
  placeProductId: z.uuid(),
  quantity: z.string().regex(/^\d+(\.\d{1,3})?$/),
  serviceQuoteItemId: z.uuid().nullable().optional(),
  serviceOrderItemId: z.uuid().nullable().optional(),
  preferredLotId: z.uuid().nullable().optional(),
  lotPickOverrideAllowed: z.boolean().optional(),
});

export const ReserveStockResponseSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('RESERVED'), reservationId: z.uuid() }),
  z.object({ kind: z.literal('WAITING_PARTS'), reason: z.literal('NO_LOCAL_STOCK') }),
]);

export const ConsumeStockBodySchema = z.object({
  placeId: z.uuid(),
  placeProductId: z.uuid(),
  quantity: z.string().regex(/^\d+(\.\d{1,3})?$/),
  allowDeferred: z.boolean().optional(),
});

export const ConsumeStockResponseSchema = z.object({
  consumptionKind: z.enum(['IMMEDIATE', 'DEFERRED']),
  movementId: z.uuid().optional(),
  deferredConsumptionId: z.uuid().optional(),
});

export type ReserveStockBody = z.infer<typeof ReserveStockBodySchema>;
export type ReserveStockResponse = z.infer<typeof ReserveStockResponseSchema>;
export const StockTransferItemResponseSchema = z.object({
  id: z.uuid(),
  placeProductId: z.uuid(),
  placeProductLotId: z.uuid(),
  originStorageId: z.uuid(),
  destinationStorageId: z.uuid(),
  quantitySent: z.string(),
  quantityReceived: z.string().nullable(),
  productName: z.string().optional(),
  productSku: z.string().nullable().optional(),
  lotCode: z.string().nullable().optional(),
  originStorageName: z.string().optional(),
  destinationStorageName: z.string().optional(),
});

export const StockTransferResponseSchema = z.object({
  id: z.uuid(),
  originPlaceId: z.uuid(),
  destinationPlaceId: z.uuid(),
  originPlaceName: z.string().optional(),
  destinationPlaceName: z.string().optional(),
  status: z.enum(['DRAFT', 'IN_TRANSIT', 'RECEIVED', 'CANCELLED']),
  requiresExternalDoc: z.boolean(),
  sentAt: z.iso.datetime().nullable(),
  receivedAt: z.iso.datetime().nullable(),
  cancelledAt: z.iso.datetime().nullable(),
  notes: z.string().nullable(),
  items: z.array(StockTransferItemResponseSchema),
});

export const CreateStockTransferBodySchema = z.object({
  originPlaceId: z.uuid(),
  destinationPlaceId: z.uuid(),
  notes: z.string().max(2000).nullable().optional(),
  items: z
    .array(
      z.object({
        placeProductId: z.uuid(),
        placeProductLotId: z.uuid(),
        originStorageId: z.uuid(),
        destinationStorageId: z.uuid(),
        quantitySent: z.string().regex(/^\d+(\.\d{1,3})?$/),
      }),
    )
    .min(1),
});

export const ReceiveStockTransferBodySchema = z.object({
  items: z
    .array(
      z.object({
        itemId: z.uuid(),
        quantityReceived: z.string().regex(/^\d+(\.\d{1,3})?$/),
      }),
    )
    .optional(),
});

export const SendTransferResponseSchema = z.object({
  stockTransferId: z.uuid(),
  status: z.literal('IN_TRANSIT'),
});

export const ReceiveTransferResponseSchema = z.object({
  stockTransferId: z.uuid(),
  status: z.literal('RECEIVED'),
  receivedAt: z.iso.datetime(),
});

export const CancelStockTransferResponseSchema = z.object({
  status: z.literal('CANCELLED'),
});

export type StockTransferResponse = z.infer<typeof StockTransferResponseSchema>;
export type CreateStockTransferBody = z.infer<typeof CreateStockTransferBodySchema>;
export type ReceiveStockTransferBody = z.infer<typeof ReceiveStockTransferBodySchema>;
export type SendTransferResponse = z.infer<typeof SendTransferResponseSchema>;
export type ReceiveTransferResponse = z.infer<typeof ReceiveTransferResponseSchema>;
export type CancelStockTransferResponse = z.infer<typeof CancelStockTransferResponseSchema>;
export type ConsumeStockBody = z.infer<typeof ConsumeStockBodySchema>;
export type ConsumeStockResponse = z.infer<typeof ConsumeStockResponseSchema>;

export const StockInventoryLineResponseSchema = z.object({
  id: z.uuid(),
  placeProductId: z.uuid(),
  placeProductLotId: z.uuid().nullable(),
  placeStorageId: z.uuid(),
  systemQuantity: z.string(),
  countedQuantity: z.string().nullable(),
  countedAt: z.iso.datetime().nullable(),
  adjustmentReason: z.string().nullable(),
  variance: z.string().nullable(),
});

export const StockInventoryResponseSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  status: z.enum(['DRAFT', 'IN_PROGRESS', 'COUNTED', 'FINALIZED']),
  cutoffAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  finalizedAt: z.iso.datetime().nullable(),
  notes: z.string().nullable(),
  lines: z.array(StockInventoryLineResponseSchema),
});

export const CreateStockInventoryBodySchema = z.object({
  placeId: z.uuid(),
  cutoffAt: z.iso.datetime(),
  storageIds: z.array(z.uuid()).min(1),
  productIds: z.array(z.uuid()).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});

export const CreateStockInventoryResponseSchema = z.object({
  stockInventoryId: z.uuid(),
});

export const RecordInventoryCountLineBodySchema = z.object({
  countedQuantity: z.string().regex(/^\d+(\.\d{1,3})?$/),
});

export const RecordInventoryCountLineResponseSchema = z.object({
  lineId: z.uuid(),
  countedAt: z.iso.datetime(),
});

export const FinalizeInventoryBodySchema = z.object({
  adjustments: z
    .array(
      z.object({
        lineId: z.uuid(),
        reason: z.string().min(1).max(50),
      }),
    )
    .optional(),
});

export const FinalizeInventoryResponseSchema = z.object({
  stockInventoryId: z.uuid(),
  status: z.literal('FINALIZED'),
  adjustmentCount: z.number().int().nonnegative(),
});

export const StartStockInventoryResponseSchema = z.object({
  status: z.literal('IN_PROGRESS'),
});

export type StockInventoryResponse = z.infer<typeof StockInventoryResponseSchema>;
export type CreateStockInventoryBody = z.infer<typeof CreateStockInventoryBodySchema>;
export type CreateStockInventoryResponse = z.infer<typeof CreateStockInventoryResponseSchema>;
export type RecordInventoryCountLineBody = z.infer<typeof RecordInventoryCountLineBodySchema>;
export type RecordInventoryCountLineResponse = z.infer<
  typeof RecordInventoryCountLineResponseSchema
>;
export type FinalizeInventoryBody = z.infer<typeof FinalizeInventoryBodySchema>;
export type FinalizeInventoryResponse = z.infer<typeof FinalizeInventoryResponseSchema>;
export type StartStockInventoryResponse = z.infer<typeof StartStockInventoryResponseSchema>;

export const QuickPhysicalReceiptBodySchema = z.object({
  placeId: z.uuid(),
  placeStorageId: z.uuid(),
  quantity: z.string().regex(/^\d+(\.\d{1,3})?$/),
  lotCode: z.string().max(100).optional(),
});

export const QuickPhysicalReceiptResponseSchema = z.object({
  stockLotId: z.uuid(),
});

export const AttachInvoiceBodySchema = z.object({
  invoiceNumber: z.string().min(1),
  invoiceAccessKey: z.string().max(100).nullable().optional(),
  unitCostCents: z.string().regex(/^\d+$/),
  currency: z.string().length(3).default('BRL'),
});

export const ReconcileDeferredConsumptionsBodySchema = z.object({
  deferredConsumptionIds: z.array(z.uuid()).min(1),
  lotId: z.uuid().optional(),
  placeId: z.uuid(),
  placeProductId: z.uuid(),
  placeStorageId: z.uuid().optional(),
  invoiceQuantity: z
    .string()
    .regex(/^\d+(\.\d{1,3})?$/)
    .optional(),
  invoiceNumber: z.string().min(1),
  invoiceAccessKey: z.string().max(100).nullable().optional(),
  unitCostCents: z.string().regex(/^\d+$/),
  currency: z.string().length(3).default('BRL'),
});

export type QuickPhysicalReceiptBody = z.infer<typeof QuickPhysicalReceiptBodySchema>;
export type QuickPhysicalReceiptResponse = z.infer<typeof QuickPhysicalReceiptResponseSchema>;
export type AttachInvoiceBody = z.infer<typeof AttachInvoiceBodySchema>;
export type ReconcileDeferredConsumptionsBody = z.infer<
  typeof ReconcileDeferredConsumptionsBodySchema
>;
